import React, { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import abdelgwadImg from '../assets/images/abdelgwad.png';
import albrawyImg from '../assets/images/albrawy.png';
import '../styles/Testimonials.css';

gsap.registerPlugin(ScrollTrigger);

const testimonials = [
  {
    id: 'abdelgwad',
    name: 'Mohammed Abdelgwad',
    role: 'Development Team Lead | Mobile App Expert',
    company: 'AMK LLC',
    context: 'Managed Momen directly',
    date: 'January 2025',
    avatar: abdelgwadImg,
    badge: 'Direct Manager',
    quote:
      "Momen is one of the best developers I've worked with. He's committed to deadlines and has very good problem solving skills.",
    highlights: ['Committed to deadlines', 'Strong problem solver', 'Reliable backend partner'],
    linkedinUrl: 'https://www.linkedin.com/in/mohammed-abdelgwad/'
  },
  {
    id: 'albrawy',
    name: 'Youssef Al-Brawy',
    role: 'Product Builder & Growth Strategist',
    company: 'Enterprise Operations',
    context: 'Collaborated on ERPNext projects',
    date: 'April 2025',
    avatar: albrawyImg,
    badge: 'Product Partner',
    quote:
      'I’ve worked alongside Mo\'men on several ERPNext customization projects. His command over the framework, attention to detail, and ability to debug complex issues are truly impressive. I’ve learned a lot from his approach and code quality.\n\nMo\'men played a critical role in transforming how we manage our operations through ERPNext. From HR and payroll to inventory and CRM, he configured everything to suit our needs. His work has helped improve our efficiency and decision-making significantly.',
    highlights: ['Command over Frappe/ERPNext', 'HR, Payroll & CRM workflows', 'Clean code quality'],
    linkedinUrl: 'https://www.linkedin.com/in/youssef-al-brawy/'
  },
  {
    id: 'sobhy',
    name: 'Mohamed Sobhy',
    role: 'CEO at Codx Pro',
    company: 'Codx Pro',
    context: 'Enterprise ERP & E-Commerce Solutions',
    date: '2025 — Present',
    initials: 'MS',
    badge: 'Executive Leadership',
    quote:
      'Momen is an exceptional Senior ERPNext engineer. At Codx Pro, he spearheaded complex enterprise pipelines for international clients like Justyol—architecting seamless Shopify-to-ERPNext synchronization and Turkish Tax Authority compliance. His deep mastery of Frappe, proactive communication, and ability to deliver rock-solid solutions under tight deadlines make him an invaluable asset to any high-stakes engineering initiative.',
    highlights: ['Shopify ↔ ERPNext architecture', 'Tax Authority compliance', 'Rock-solid under tight deadlines'],
    companyUrl: 'https://justyol.com'
  }
];

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const stageRef = useRef(null);

  useEffect(() => {
    if (stageRef.current) {
      stageRef.current.scrollLeft = 0;
    }
  }, [activeIndex]);

  const prevSlide = useCallback(() => {
    setActiveIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  }, []);

  const nextSlide = useCallback(() => {
    setActiveIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  }, []);

  const goToSlide = (index) => {
    setActiveIndex(index);
  };

  // Keyboard navigation (ArrowLeft & ArrowRight)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'ArrowRight') nextSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevSlide, nextSlide]);

  // Touch swipe support
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (stageRef.current) {
      stageRef.current.scrollLeft = 0;
    }
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 45;
    const isRightSwipe = distance < -45;
    if (isLeftSwipe) nextSlide();
    if (isRightSwipe) prevSlide();
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      if (titleRef.current) {
        gsap.from(titleRef.current.children, {
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
            toggleActions: 'play none none none'
          },
          y: 40,
          opacity: 0,
          stagger: 0.15,
          duration: 0.8,
          ease: 'power3.out',
          clearProps: 'transform,opacity'
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="testimonials" id="testimonials">
      <div className="testimonials-container">
        {/* Header */}
        <div ref={titleRef} className="testimonials-header">
          <span className="testimonials-label">Social Proof & Recommendations</span>
          <h2 className="testimonials-title">Client & Team Endorsements</h2>
          <p className="testimonials-subtitle">
            Authentic recommendations from engineering team leads, product builders, and CEOs who have worked directly with me.
          </p>
        </div>

        {/* Carousel Showcase Container */}
        <div
          className="testimonials-carousel"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Previous Arrow Button (Desktop Side Navigation) */}
          <button
            type="button"
            className="carousel-arrow-btn prev-btn desktop-only"
            onClick={prevSlide}
            aria-label="Previous testimonial"
            title="Previous testimonial (Left Arrow)"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          {/* Carousel Viewport / Stage */}
          <div ref={stageRef} className="carousel-stage">
            <div
              className="carousel-track"
              style={{
                transform: `translateX(-${activeIndex * 100}%)`
              }}
            >
              {testimonials.map((item, idx) => (
                <div
                  key={item.id}
                  className={`carousel-slide ${activeIndex === idx ? 'is-active' : ''}`}
                  onClick={() => goToSlide(idx)}
                >
                  <div className="testimonial-card">
                    <div className="testimonial-card-top">
                      <div className="testimonial-author">
                        {item.avatar ? (
                          <img
                            src={item.avatar}
                            alt={item.name}
                            className="testimonial-avatar"
                            loading="lazy"
                          />
                        ) : (
                          <div className="testimonial-avatar-initials">
                            {item.initials}
                          </div>
                        )}

                        <div className="testimonial-author-info">
                          <div className="testimonial-name-row">
                            <h3 className="testimonial-name">{item.name}</h3>
                            <span className="testimonial-verified" title="Verified Colleague Recommendation">
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                              </svg>
                            </span>
                          </div>
                          <p className="testimonial-role">{item.role}</p>
                          <div className="testimonial-meta">
                            <span className="testimonial-context">{item.context}</span>
                            <span className="testimonial-dot">•</span>
                            <span className="testimonial-date">{item.date}</span>
                          </div>
                        </div>
                      </div>

                      <div className="testimonial-badge">{item.badge}</div>
                    </div>

                    <div className="testimonial-quote-wrap">
                      <span className="testimonial-quote-icon">“</span>
                      <p className="testimonial-quote-text">{item.quote}</p>
                    </div>

                    {item.highlights && item.highlights.length > 0 && (
                      <div className="testimonial-highlights">
                        {item.highlights.map((highlight, i) => (
                          <span key={i} className="testimonial-pill">
                            ✓ {highlight}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Next Arrow Button (Desktop Side Navigation) */}
          <button
            type="button"
            className="carousel-arrow-btn next-btn desktop-only"
            onClick={nextSlide}
            aria-label="Next testimonial"
            title="Next testimonial (Right Arrow)"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>

        {/* Carousel Pagination & Indicator Controls */}
        <div className="carousel-controls">
          <button
            type="button"
            className="carousel-control-arrow prev-control"
            onClick={prevSlide}
            aria-label="Previous testimonial"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          <div className="carousel-dots">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                type="button"
                className={`carousel-dot ${activeIndex === idx ? 'active' : ''}`}
                onClick={() => goToSlide(idx)}
                aria-label={`Go to testimonial ${idx + 1}`}
              />
            ))}
          </div>

          <div className="carousel-counter">
            <span className="counter-current">0{activeIndex + 1}</span>
            <span className="counter-divider">/</span>
            <span className="counter-total">0{testimonials.length}</span>
          </div>

          <button
            type="button"
            className="carousel-control-arrow next-control"
            onClick={nextSlide}
            aria-label="Next testimonial"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>

        {/* LinkedIn Connection Invitation */}
        <div className="testimonials-cta">
          <p className="testimonials-cta-text">
            Want to verify these recommendations or see mutual connections?
          </p>
          <a
            href="https://www.linkedin.com/in/momenmedhatsalem/"
            target="_blank"
            rel="noopener noreferrer"
            className="testimonials-linkedin-btn"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
            </svg>
            Connect with Momen on LinkedIn ↗
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
